import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Alert, Spinner } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import HeaderTwo from "../../components/HeaderTwo";
import FooterTwo from "../../components/FooterTwo";
import { BreadcrumbBox } from "../../components/common/Breadcrumb";
import { Styles } from "./styles/adminDashboard.js";
import { useAuth } from "../../context/AuthContext";
import { ADMIN_APPLICATIONS_SUMMARY_ENDPOINT } from "../../config/api";

const createEmptySummary = () => ({
  totals: { applications: 0 },
  intakes: [],
  agents: [],
  fetchedAt: null,
});

const AdminDashboard = () => {
  const history = useHistory();
  const { user, loading: authLoading } = useAuth();
  const [summary, setSummary] = useState(() => createEmptySummary());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isAdmin = useMemo(() => {
    if (!Array.isArray(user?.roles)) {
      return false;
    }
    return user.roles.some(
      (role) => typeof role === "string" && role.toLowerCase() === "admin"
    );
  }, [user]);

  useEffect(() => {
    if (!authLoading && !user) {
      history.push(`${process.env.PUBLIC_URL || ""}/login`);
    }
  }, [authLoading, user, history]);

  useEffect(() => {
    if (authLoading || !isAdmin) {
      return;
    }

    let isMounted = true;

    const fetchSummary = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          throw new Error("Authentication required.");
        }

        const response = await fetch(ADMIN_APPLICATIONS_SUMMARY_ENDPOINT, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.message || "Unable to load dashboard data.");
        }

        const payload = await response.json();
        if (!isMounted) {
          return;
        }

        const totals = payload?.totals || {};
        const totalApplications = Number(totals.applications);
        setSummary({
          totals: {
            applications: Number.isFinite(totalApplications)
              ? totalApplications
              : 0,
          },
          intakes: Array.isArray(payload?.intakes) ? payload.intakes : [],
          agents: Array.isArray(payload?.agents) ? payload.agents : [],
          fetchedAt: payload?.fetchedAt || null,
        });
      } catch (err) {
        if (isMounted) {
          setError(err.message || "Unable to load dashboard data.");
          setSummary(createEmptySummary());
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSummary();

    return () => {
      isMounted = false;
    };
  }, [authLoading, isAdmin]);

  const formattedFetchedAt = useMemo(() => {
    if (!summary.fetchedAt) {
      return "";
    }
    const date = new Date(summary.fetchedAt);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    return date.toLocaleString();
  }, [summary.fetchedAt]);

  const handleNavigate = (params) => {
    const base = `${process.env.PUBLIC_URL || ""}/admin/applications`;
    const searchParams = new URLSearchParams();
    if (params.intakeId) {
      searchParams.set("intakeId", params.intakeId);
    }
    if (params.agentId) {
      searchParams.set("agentId", params.agentId);
    }
    const suffix = searchParams.toString();
    history.push(`${base}${suffix ? `?${suffix}` : ""}`);
  };

  return (
    <Styles>
      <div className="main-wrapper admin-dashboard-page">
        <HeaderTwo />
        <BreadcrumbBox title="Admin Dashboard" />

        <section className="dashboard-area">
          <Container>
            <Row>
              <Col md="12">
                {!authLoading && !isAdmin && (
                  <Alert variant="danger">
                    You do not have permission to view this page.
                  </Alert>
                )}

                {isAdmin && (
                  <div className="dashboard-card">
                    {error && (
                      <Alert variant="danger" className="mb-4">
                        {error}
                      </Alert>
                    )}

                    {loading ? (
                      <div className="loading-state">
                        <Spinner animation="border" role="status" />
                      </div>
                    ) : (
                      <div className="dashboard-content">
                        <div className="summary-cards">
                          <div className="summary-card total-applications">
                            <span className="summary-label">
                              Total Applications
                            </span>
                            <span className="summary-value">
                              {summary.totals?.applications ?? 0}
                            </span>
                            {formattedFetchedAt && (
                              <span className="summary-meta">
                                Updated {formattedFetchedAt}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="stat-section">
                          <div className="section-header">
                            <h3>Applications by Intake</h3>
                            <p>
                              Click a card to view applications for that intake.
                            </p>
                          </div>
                          {summary.intakes.length === 0 ? (
                            <div className="empty-state">
                              No applications with linked intakes yet.
                            </div>
                          ) : (
                            <div className="stat-grid">
                              {summary.intakes.map((intake) => {
                                const labelParts = [
                                  intake.name || "Intake",
                                ];
                                if (Number.isFinite(intake.year)) {
                                  labelParts.push(intake.year);
                                }
                                return (
                                  <div
                                    key={intake.id}
                                    role="button"
                                    tabIndex={0}
                                    className="stat-card"
                                    onClick={() =>
                                      handleNavigate({ intakeId: intake.id })
                                    }
                                    onKeyDown={(event) => {
                                      if (
                                        event.key === "Enter" ||
                                        event.key === " "
                                      ) {
                                        event.preventDefault();
                                        handleNavigate({
                                          intakeId: intake.id,
                                        });
                                      }
                                    }}
                                  >
                                    <div className="stat-card-header">
                                      <span className="stat-title">
                                        {labelParts.join(" ")}
                                      </span>
                                      <span
                                        className={`status-pill status-${(
                                          intake.status || "unknown"
                                        ).toLowerCase()}`}
                                      >
                                        {(intake.status || "Unknown")
                                          .toString()
                                          .replace(/_/g, " ")}
                                      </span>
                                    </div>
                                    <div className="stat-count">
                                      {intake.count ?? 0}
                                    </div>
                                    {(intake.startDate || intake.endDate) && (
                                      <div className="stat-meta">
                                        {[
                                          intake.startDate
                                            ? new Date(
                                                intake.startDate
                                              ).toLocaleDateString()
                                            : "",
                                          intake.endDate
                                            ? new Date(
                                                intake.endDate
                                              ).toLocaleDateString()
                                            : "",
                                        ]
                                          .filter(Boolean)
                                          .join(" – ")}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        <div className="stat-section">
                          <div className="section-header">
                            <h3>Applications by Agent</h3>
                            <p>
                              Identify top-performing agents and review their
                              application pipeline.
                            </p>
                          </div>
                          {summary.agents.length === 0 ? (
                            <div className="empty-state">
                              No applications are linked to agents yet.
                            </div>
                          ) : (
                            <div className="stat-grid">
                              {summary.agents.map((agent) => (
                                <div
                                  key={agent.id}
                                  role="button"
                                  tabIndex={0}
                                  className="stat-card agent-card"
                                  onClick={() =>
                                    handleNavigate({ agentId: agent.id })
                                  }
                                  onKeyDown={(event) => {
                                    if (
                                      event.key === "Enter" ||
                                      event.key === " "
                                    ) {
                                      event.preventDefault();
                                      handleNavigate({ agentId: agent.id });
                                    }
                                  }}
                                >
                                  <div className="stat-card-header">
                                    <span className="stat-title">
                                      {agent.name || "Agent"}
                                    </span>
                                    <span
                                      className={`status-pill status-${(
                                        agent.status || "unknown"
                                      ).toLowerCase()}`}
                                    >
                                      {(agent.status || "Unknown")
                                        .toString()
                                        .replace(/_/g, " ")}
                                    </span>
                                  </div>
                                  {agent.email && (
                                    <div className="stat-meta agent-email">
                                      {agent.email}
                                    </div>
                                  )}
                                  <div className="stat-count">
                                    {agent.count ?? 0}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </section>

        <FooterTwo />
      </div>
    </Styles>
  );
};

export default AdminDashboard;
